import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { payoutAPI } from '../services/api';
import './EarlyPayout.css';

const EarlyPayout = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState('STANDARD_BANK_INSTANT');
  const [formData, setFormData] = useState({
    amount: '',
    bankAccountNumber: '',
    bankAccountHolder: '',
    bankBranchCode: '',
    ewalletPhoneNumber: '',
    ewalletRecipientName: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchPayoutData();
  }, []);

  const fetchPayoutData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats
      const statsResponse = await payoutAPI.getPayoutStats();
      setStats(statsResponse.data);

      // Fetch history
      const historyResponse = await payoutAPI.getPayoutHistory();
      setPayoutHistory(historyResponse.data);
    } catch (err) {
      console.error('Error fetching payout data:', err);
      setError('Failed to load payout data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const requestData = {
        amount: parseInt(formData.amount),
        payoutMethod: payoutMethod
      };

      if (payoutMethod === 'STANDARD_BANK_INSTANT') {
        requestData.bankAccountNumber = formData.bankAccountNumber;
        requestData.bankAccountHolder = formData.bankAccountHolder;
        requestData.bankBranchCode = formData.bankBranchCode;
      } else {
        requestData.ewalletPhoneNumber = formData.ewalletPhoneNumber;
        requestData.ewalletRecipientName = formData.ewalletRecipientName;
      }

      const response = await payoutAPI.requestPayout(requestData);

      setSuccess('Payout request submitted successfully!');
      setShowRequestForm(false);
      setFormData({
        amount: '',
        bankAccountNumber: '',
        bankAccountHolder: '',
        bankBranchCode: '',
        ewalletPhoneNumber: '',
        ewalletRecipientName: ''
      });
      
      // Refresh data
      fetchPayoutData();
    } catch (err) {
      console.error('Error requesting payout:', err);
      setError(err.response?.data?.error || err.message || 'Failed to request payout');
    }
  };

  const cancelPayout = async (payoutId) => {
    if (!window.confirm('Are you sure you want to cancel this payout?')) {
      return;
    }

    try {
      await payoutAPI.cancelPayout(payoutId);
      setSuccess('Payout cancelled successfully');
      fetchPayoutData();
    } catch (err) {
      console.error('Error cancelling payout:', err);
      setError(err.response?.data?.error || err.message || 'Failed to cancel payout');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      PENDING: 'status-pending',
      PROCESSING: 'status-processing',
      COMPLETED: 'status-completed',
      FAILED: 'status-failed',
      CANCELLED: 'status-cancelled'
    };

    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>;
  };

  if (loading) {
    return <div className="loading">Loading payout information...</div>;
  }

  return (
    <div className="early-payout-container">
      <h1>Early Payout</h1>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Stats Section */}
      {stats && (
        <div className="payout-stats">
          <div className="stat-card">
            <h3>Available Balance</h3>
            <p className="stat-value">R{stats.availableBalance}</p>
          </div>
          <div className="stat-card">
            <h3>Total Paid Out</h3>
            <p className="stat-value">R{stats.totalPaidOut}</p>
          </div>
          <div className="stat-card">
            <h3>Pending Payouts</h3>
            <p className="stat-value">R{stats.pendingPayouts}</p>
          </div>
        </div>
      )}

      {/* Request Payout Button */}
      {!showRequestForm && (
        <button 
          className="btn-primary"
          onClick={() => setShowRequestForm(true)}
          disabled={stats && stats.availableBalance < stats.minimumPayoutAmount}
        >
          Request Payout
        </button>
      )}

      {stats && stats.availableBalance < stats.minimumPayoutAmount && (
        <p className="info-text">
          Minimum payout amount is R{stats.minimumPayoutAmount}. 
          Current balance: R{stats.availableBalance}
        </p>
      )}

      {/* Payout Request Form */}
      {showRequestForm && (
        <div className="payout-form-container">
          <h2>Request Payout</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Amount (R)</label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                min={stats?.minimumPayoutAmount || 50}
                max={stats?.availableBalance || 0}
                required
              />
              <small>Available: R{stats?.availableBalance || 0}</small>
            </div>

            <div className="form-group">
              <label>Payout Method</label>
              <select 
                value={payoutMethod} 
                onChange={(e) => setPayoutMethod(e.target.value)}
              >
                <option value="STANDARD_BANK_INSTANT">Standard Bank Instant Payment</option>
                <option value="FNB_EWALLET">FNB eWallet</option>
              </select>
            </div>

            {payoutMethod === 'STANDARD_BANK_INSTANT' && (
              <>
                <div className="form-group">
                  <label>Account Number</label>
                  <input
                    type="text"
                    name="bankAccountNumber"
                    value={formData.bankAccountNumber}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Account Holder Name</label>
                  <input
                    type="text"
                    name="bankAccountHolder"
                    value={formData.bankAccountHolder}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Branch Code</label>
                  <input
                    type="text"
                    name="bankBranchCode"
                    value={formData.bankBranchCode}
                    onChange={handleInputChange}
                    placeholder="e.g., 051001"
                    required
                  />
                </div>
              </>
            )}

            {payoutMethod === 'FNB_EWALLET' && (
              <>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="ewalletPhoneNumber"
                    value={formData.ewalletPhoneNumber}
                    onChange={handleInputChange}
                    placeholder="0821234567"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Recipient Name</label>
                  <input
                    type="text"
                    name="ewalletRecipientName"
                    value={formData.ewalletRecipientName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </>
            )}

            <div className="form-actions">
              <button type="submit" className="btn-primary">Submit Request</button>
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => setShowRequestForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Payout History */}
      <div className="payout-history">
        <h2>Payout History</h2>
        {payoutHistory.length === 0 ? (
          <p>No payout history yet</p>
        ) : (
          <div className="history-list">
            {payoutHistory.map((payout) => (
              <div key={payout.id} className="history-item">
                <div className="history-header">
                  <span className="amount">R{payout.amount}</span>
                  {getStatusBadge(payout.status)}
                </div>
                <div className="history-details">
                  <p><strong>Method:</strong> {payout.payoutMethod.replace('_', ' ')}</p>
                  <p><strong>Reference:</strong> {payout.transactionReference}</p>
                  {payout.transactionId && (
                    <p><strong>Transaction ID:</strong> {payout.transactionId}</p>
                  )}
                  {payout.maskedAccountNumber && (
                    <p><strong>Account:</strong> {payout.maskedAccountNumber}</p>
                  )}
                  {payout.maskedPhoneNumber && (
                    <p><strong>Phone:</strong> {payout.maskedPhoneNumber}</p>
                  )}
                  <p><strong>Recipient:</strong> {payout.recipientName}</p>
                  <p><strong>Requested:</strong> {new Date(payout.createdAt).toLocaleString()}</p>
                  {payout.processedAt && (
                    <p><strong>Processed:</strong> {new Date(payout.processedAt).toLocaleString()}</p>
                  )}
                  {payout.failureReason && (
                    <p className="error-text"><strong>Failure Reason:</strong> {payout.failureReason}</p>
                  )}
                </div>
                {payout.status === 'PENDING' && (
                  <button 
                    className="btn-cancel"
                    onClick={() => cancelPayout(payout.id)}
                  >
                    Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EarlyPayout;
